import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Sun, Moon, Menu, X, MapPin, Phone, Mail, Facebook, Instagram, Twitter, Linkedin, MessageCircle, Package, Building, Rocket, Search, Box, Truck } from 'lucide-react';

const navItems = [
  { label: 'Expédier un colis', to: '/expedier',  icon: Package },
  { label: 'Devenir Point Relais', to: '/devenir-relais',  icon: Building },
  { 
    label: 'Solutions Pro', 
    to: '/solutions',
    icon: Rocket
  },
  { label: 'Fonctionnalités', to: '/fonctionnalites', hidden: true },
  { label: 'Pour qui ?', to: '/pour-qui', hidden: true },
  { label: 'Tarifs', to: '/tarifs', hidden: true },
  { label: 'Partenaires', to: '/partenaires', hidden: true },
  { label: 'À propos', to: '/a-propos', hidden: true },
  { label: 'Support & Aide', to: '/support', hidden: true },
  { label: 'Contact', to: '/contact', hidden: true },
];

export default function SiteLayout({ children }) {
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // const [solutionsSubmenuOpen, setSolutionsSubmenuOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');

  // Tracking modal state
  const [showTracking, setShowTracking] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [isTrackingLoading, setIsTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  // Aliases pour compatibilité avec le modal collé depuis Home.js
  const showTrackingModal = showTracking;
  const setShowTrackingModal = setShowTracking;
  const relayName = (trackingResult?.relay_name 
    || trackingResult?.point_relais?.name 
    || trackingResult?.point_relais_nom 
    || (trackingResult?.pointrelais != null ? String(trackingResult?.pointrelais) : '—'));
  const carrierName = (trackingResult?.carrier_name 
    || trackingResult?.transporteur?.nom 
    || trackingResult?.transporteur_nom 
    || (trackingResult?.transporteur != null ? String(trackingResult?.transporteur) : '—'));

  const isHome = location.pathname === '/';

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // Logique pour l'inscription à la newsletter
    console.log('Newsletter subscription:', newsletterEmail);
    setNewsletterEmail('');
  };

  // Helpers inspirés de Tracking.js
  const initialAddress = {
    name: 'Non renseigné',
    phone: 'Non renseigné',
    email: 'Non renseigné',
    address: 'Non renseigné',
    company: ''
  };

  const STATUS_LABELS = {
    '1': 'Planifié',
    '2': 'En cours d\'affectation',
    '3': 'En chemin vers le lieu de ramassage',
    '4': 'Arrivé au lieu de ramassage',
    '5': 'En chemin vers la destination',
    '6': 'Arrivé à destination',
    '7': 'Retour au magasin',
    '8': 'Terminé',
    '9': 'Livraison annulée',
  };

  const normalizeStatusCode = (status) => {
    if (status === null || status === undefined) return '';
    const statusStr = String(status).trim();
    if (STATUS_LABELS[statusStr]) {
      return statusStr;
    }
    const entry = Object.entries(STATUS_LABELS).find(([, label]) => label.toLowerCase() === statusStr.toLowerCase());
    return entry ? entry[0] : statusStr;
  };

  const getStatusLabel = (status) => {
    const code = normalizeStatusCode(status);
    return STATUS_LABELS[code] || (status ?? '—');
  };

  const getStatusColor = (status) => {
    const code = normalizeStatusCode(status);
    switch (code) {
      case '1':
      case '2':
        return 'bg-yellow-500';
      case '3':
      case '4':
        return 'bg-orange-500';
      case '5':
      case '6':
        return 'bg-blue-500';
      case '7':
        return 'bg-purple-500';
      case '8':
        return 'bg-green-500';
      case '9':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const extractAddressInfo = (addressObj) => {
    if (!addressObj) return initialAddress;

    if (typeof addressObj === 'string') {
      return {
        name: addressObj,
        phone: 'Non renseigné',
        email: 'Non renseigné',
        address: addressObj,
        company: ''
      };
    }

    if (typeof addressObj === 'object') {
      return {
        name: `${addressObj.customer_first_name || ''} ${addressObj.customer_last_name || ''}`.trim() ||
          `${addressObj.recipient_first_name || ''} ${addressObj.recipient_last_name || ''}`.trim() ||
          addressObj.name || 'Non renseigné',
        phone: addressObj.customer_phone_number || addressObj.pickup_phone_number || 'Non renseigné',
        email: addressObj.customer_email || addressObj.recipient_email || addressObj.pickup_email || addressObj.email || 'Non renseigné',
        address: addressObj.address || addressObj.full_address || 'Non renseigné',
        company: addressObj.pickup_company_name || addressObj.company_name || ''
      };
    }

    return initialAddress;
  };

  // Submit tracking using existing API endpoint already used on Home
  const handleTrackingSubmit = async (e) => {
    e.preventDefault();
    setTrackingError('');
    setTrackingResult(null);
    if (!trackingNumber.trim()) {
      setTrackingError('Veuillez saisir un numéro de suivi');
      toast.error('Numéro de suivi invalide');
      return;
    }
    try {
      setIsTrackingLoading(true);
      // Appel direct de l'endpoint backend
      const resp = await fetch(`https://backend.katianlogistique.com/api/client/expedition/${encodeURIComponent(trackingNumber.trim())}/`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'omit'
      });
      if (!resp.ok) {
        // toast.error('Numéro de suivi invalide');
        throw new Error('Not OK');
      }
      const data = await resp.json();
      setTrackingResult(data);
    } catch (err) {
      setTrackingError('Aucun colis trouvé avec se numero ou erreur réseau.');
      toast.error('Numéro de suivi invalide');
    } finally {
      setIsTrackingLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-dark-bg transition-colors duration-300">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/95 dark:bg-dark-bg/95 shadow-sm border-b border-gray-100 dark:border-gray-800 backdrop-blur supports-[backdrop-filter]:bg-white/90 transition-colors">
        <div className="container-ksl flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img src="/katian-logo.png" alt="Katian" className="h-8 w-auto" />
          </Link>

          {/* Navigation Desktop */}
          <nav className="hidden lg:flex items-center space-x-4">
            {navItems.filter(item => !item.hidden).map((item) => (
              <div key={item.to} className="relative">
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                        `px-4 py-2 text-sm font-bold rounded-lg transition-colors duration-200 whitespace-nowrap flex items-center space-x-3 ${
                        isActive 
                          ? 'bg-ksl-red text-white' 
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary hover:text-gray-900 dark:hover:text-white'
                      }`
                }
              >
                  <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
              </div>
            ))}
            {!isHome && (
              <button
                onClick={() => setShowTracking(true)}
                className="px-4 py-2 text-sm font-bold rounded-lg transition-colors duration-200 whitespace-nowrap flex items-center space-x-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary hover:text-gray-900 dark:hover:text-white"
              >
                <Search className="w-4 h-4" />
                Suivre un colis
              </button>
            )}
          </nav>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary transition-colors"
              title={isDark ? 'Passer en mode clair' : 'Passer en mode sombre'}
            >
              {isDark ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-gray-700" />}
            </button>
            
            {/* Boutons d'action */}
            <div className="hidden md:flex items-center space-x-2">
              <a 
                href="https://business.katianlogistique.com" 
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary transition-colors duration-200 whitespace-nowrap min-w-[100px] text-center"
              >
                Se connecter
              </a>
              <Link 
                to="/register" 
                className="px-3 py-2 text-sm font-medium bg-ksl-red text-white rounded-lg hover:bg-ksl-red-dark transition-colors duration-200 shadow-sm whitespace-nowrap min-w-[120px] text-center"
              >
                Créer un compte
              </Link>
            </div>

            {/* Menu mobile */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary transition-colors"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Menu mobile */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-dark-bg">
            <div className="container-ksl py-4 space-y-3">
              {navItems.filter(item => !item.hidden).map((item) => (
                <div key={item.to} className="relative">
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                      `block w-full px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 flex items-center space-x-3 ${
                          isActive 
                            ? 'bg-ksl-red text-white' 
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary'
                        }`
                      }
                      onClick={() => setMobileMenuOpen(false)}
                    >
                    <item.icon className="w-5 h-5" />
                      {item.label}
                    </NavLink>
                </div>
              ))}
              {!isHome && (
                <button
                  onClick={() => { setShowTracking(true); setMobileMenuOpen(false); }}
                  className="block w-full px-4 py-3 text-left text-sm font-medium rounded-lg transition-colors duration-200 flex items-center space-x-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary"
                >
                  <Search className="w-5 h-5" />
                  Suivre un colis
                </button>
              )}
              <div className="pt-4 space-y-3">
                <a 
                  href="https://business.katianlogistique.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary transition-colors duration-200 text-center whitespace-nowrap"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Se connecter
                </a>
                <Link 
                  to="/register" 
                  className="block w-full px-4 py-2 text-sm font-medium bg-ksl-red text-white rounded-lg hover:bg-ksl-red-dark transition-colors duration-200 text-center shadow-sm whitespace-nowrap"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Créer un compte
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Contenu */}
      <main className="flex-1">{children}</main>

      {/* Modal de suivi colis */}
      {showTracking && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" onClick={() => { setShowTracking(false); setTrackingResult(null); setTrackingError(''); }}>
          <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-xl w-full max-w-6xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Suivre un colis {trackingResult?.expedition_number ? `- ${trackingResult?.expedition_number}` : ''}
              </h3>
              <button onClick={() => { setShowTracking(false); setTrackingResult(null); setTrackingNumber(''); setTrackingError(''); }} className="p-2 rounded hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleTrackingSubmit} className="space-y-3">
              <input
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="Ex: ABC00000000123456789"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ksl-red"
              />
              {trackingError && <div className="text-sm text-red-600">{trackingError}</div>}
              <div className="flex justify-end">
                <button type="submit" disabled={isTrackingLoading} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
                  {isTrackingLoading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  {isTrackingLoading ? 'Recherche...' : 'Suivre'}
                </button>
              </div>
            </form>
            {/* Résultat détaillé - Identique à Tracking.js */}
      {showTrackingModal && trackingResult && (() => {
        const senderInfo = extractAddressInfo(trackingResult.adresse_expediteur);
        const recipientInfo = extractAddressInfo(trackingResult.adresse_destinataire);
        const parcels = trackingResult?.infocolis || [];
        const statusRecep = trackingResult?.statut_recep || '';
        const statusExp = trackingResult?.statut_exp || '';
        const globalStatus = trackingResult?.statut_colis || '';
        const withdrawalCode = trackingResult?.code_retrait || trackingResult?.withdrawal_code || '';
        const distanceKm = trackingResult?.dataApi?.merged_est_dist_km || '--';
        const durationMinutes = trackingResult?.dataApi?.merged_est_minutes || '--';
        const deliveryEta = trackingResult?.delais_livraison || '--';
        const modeExpedition = trackingResult?.mode_expedition || '';
        
        const statusCode = normalizeStatusCode(globalStatus);
        const isReturnStatus = statusCode === '7';
        const isCancelledStatus = statusCode === '9';
        
        // Déterminer si on affiche l'étape "Arrivée à destination"
        const isRelayMode = modeExpedition?.toLowerCase().includes('relais') || modeExpedition?.toLowerCase().includes('point relais');
        const isDomicileMode = modeExpedition?.toLowerCase().includes('domicile');
        
        // Calcul des étapes selon le mode d'expédition
        let step1Completed, step2Completed, step3Completed, step3InTransit, step3InProgress;
        
        if (isRelayMode) {
          const statusExpLower = statusExp?.toLowerCase().trim() || '';
          const statusRecepLower = statusRecep?.toLowerCase().trim() || '';
          
          step1Completed = ['à expédier', 'a expédier', 'a expedier', 'à expedier', 'en transit', 'expédié', 'expedie'].includes(statusExpLower);
          step2Completed = ['à expédier', 'a expédier', 'a expedier', 'à expedier', 'en transit', 'expédié', 'expedie'].includes(statusExpLower);
          step3Completed = ['expédié', 'expedie'].includes(statusExpLower) || statusRecepLower === 'receptionné';
          step3InTransit = statusExpLower === 'en transit';
          step3InProgress = !step3Completed && step3InTransit;
        } else {
          step1Completed = [
            'Planifié',
            "En cours d'affectation",
            'En chemin vers le lieu de ramassage',
            'En chemin vers la destination',
            'Arrivé à destination',
            'Terminé',
            'Retour au magasin',
            'Livraison annulée',
          ].includes(globalStatus);
          
          step2Completed = [
            'En chemin vers la destination',
            'Arrivé à destination',
            'Terminé',
            'Retour au magasin',
            'Livraison annulée',
          ].includes(globalStatus);
          
          step3Completed = [
            'Arrivé à destination',
            'Terminé',
            'Retour au magasin',
            'Livraison annulée',
          ].includes(globalStatus);
          
          step3InTransit = ['En chemin vers la destination'].includes(globalStatus);
          step3InProgress = !step3Completed && step3InTransit;
        }
        
        // Logique adaptée selon le mode d'expédition
        let step4Reached, step4Completed, step4InProgress, step5Completed, step5InProgress;
        let totalSteps;
        let step2InProgress;
        
        if (isRelayMode) {
          const statusExpLower = statusExp?.toLowerCase().trim() || '';
          const statusRecepLower = statusRecep?.toLowerCase().trim() || '';
          
          const isDelivered = (statusExpLower === 'expédié' || statusExpLower === 'expedie') || 
                              (statusRecepLower === 'livré' || statusRecepLower === 'livre');
          
          if (isDelivered) {
            step4Reached = true;
            step4Completed = true;
            step4InProgress = false;
            step5Completed = false;
            step5InProgress = false;
            totalSteps = 4;
          } else {
            step4Reached = statusRecepLower === 'receptionné';
            step4Completed = false;
            step4InProgress = step4Reached;
            step5Completed = false;
            step5InProgress = false;
            totalSteps = 4;
          }
          
          step2InProgress = ['à expédier', 'a expédier', 'a expedier', 'à expedier'].includes(statusExpLower) && !step3InTransit;
        } else {
          step4Reached = false;
          step4Completed = false;
          step4InProgress = false;
          step5Completed = statusRecep === 'livré' && statusCode === '8';
          step5InProgress = !step5Completed && (statusCode === '8' || isReturnStatus || isCancelledStatus);
          totalSteps = 4;
          
          step2InProgress = !step2Completed && (globalStatus === 'Arrivé au lieu de ramassage');
        }
        
        const deliveryCardTitle = isCancelledStatus ? 'Livraison annulée' : isReturnStatus ? 'Retour au magasin' : 'Livraison';
        const deliveryStepVariant = isCancelledStatus ? 'cancelled' : isReturnStatus ? 'return' : 'default';
        
        const arrivalBadgeLabel = step4Completed
          ? '✅ Arrivé'
          : step4InProgress
          ? '📍 Arrivé'
          : '⏳ En attente';
        const arrivalMessageText = step4Completed || step4InProgress
          ? "Le colis est bien arrivé à destination."
          : "Le colis sera marqué arrivé dès qu'il atteindra la destination.";
        
        const deliveryBadgeLabel = step5Completed
          ? '✅ Terminé'
          : isCancelledStatus
          ? '❌ Annulé'
          : isReturnStatus
          ? '↩️ Retour'
          : '⏳ En attente';
        const deliveryMessageText = step5Completed
          ? "Votre colis a été livré avec succès à l'adresse de destination."
          : isCancelledStatus
          ? 'La livraison a été annulée. Veuillez nous contacter pour définir les prochaines étapes.'
          : isReturnStatus
          ? 'Le colis est retourné au magasin. Contactez-nous pour programmer un nouvel envoi ou un retrait.'
          : 'Le colis sera livré une fois le transit terminé.';

        // Calcul de progression adapté selon le mode
        let completedSteps, progressIndexValue;
        
        if (isRelayMode) {
          const statusExpLower = statusExp?.toLowerCase().trim() || '';
          const statusRecepLower = statusRecep?.toLowerCase().trim() || '';
          const isDelivered = (statusExpLower === 'expédié' || statusExpLower === 'expedie') || 
                              (statusRecepLower === 'livré' || statusRecepLower === 'livre');
          
          if (isDelivered) {
            completedSteps = [step1Completed, step2Completed, step3Completed, true].filter(Boolean).length;
            progressIndexValue = completedSteps;
          } else {
            completedSteps = [step1Completed, step2Completed, step3Completed, step4Completed].filter(Boolean).length;
            progressIndexValue = completedSteps;
            if (step4InProgress) progressIndexValue = Math.max(progressIndexValue, 3.5);
          }
        } else {
          completedSteps = [step1Completed, step2Completed, step3Completed, step5Completed].filter(Boolean).length;
          progressIndexValue = completedSteps;
          if (step5InProgress) progressIndexValue = Math.max(progressIndexValue, 3.5);
        }
        
        if (!step1Completed) progressIndexValue = Math.max(progressIndexValue, 0.5);
        if (step2InProgress) progressIndexValue = Math.max(progressIndexValue, 1.5);
        if (step3InProgress) progressIndexValue = Math.max(progressIndexValue, 2.5);

        const completedSegments = Math.max(0, progressIndexValue - 1);
        const progressLineFill = totalSteps > 1
          ? Math.min(100, Math.max(0, (completedSegments / (totalSteps - 1)) * 100))
          : 0;

        // Timeline adaptée selon le mode d'expédition
        let stepsTimeline;
        
        if (isRelayMode) {
          const statusExpLower = statusExp?.toLowerCase().trim() || '';
          const statusRecepLower = statusRecep?.toLowerCase().trim() || '';
          const isDelivered = (statusExpLower === 'expédié' || statusExpLower === 'expedie') || 
                              (statusRecepLower === 'livré' || statusRecepLower === 'livre');
          
          if (isDelivered) {
            stepsTimeline = [
              { key: 'ordered', title: '', Icon: Box, completed: step1Completed, inProgress: !step1Completed, variant: 'default' },
              { key: 'pickup', title: '', Icon: MapPin, completed: step2Completed, inProgress: step2InProgress, variant: 'default' },
              { key: 'transit', title: '', Icon: Truck, completed: step3Completed, inProgress: step3InProgress, variant: 'default' },
              { key: 'delivery', title: '', Icon: Package, completed: true, inProgress: false, variant: deliveryStepVariant },
            ];
          } else {
            stepsTimeline = [
              { key: 'ordered', title: '', Icon: Box, completed: step1Completed, inProgress: !step1Completed, variant: 'default' },
              { key: 'pickup', title: '', Icon: MapPin, completed: step2Completed, inProgress: step2InProgress, variant: 'default' },
              { key: 'transit', title: '', Icon: Truck, completed: step3Completed, inProgress: step3InProgress, variant: 'default' },
              { key: 'arrival', title: '', Icon: MapPin, completed: step4Completed || step4InProgress, inProgress: step4InProgress, variant: 'arrival' },
            ];
          }
        } else {
          stepsTimeline = [
            { key: 'ordered', title: '', Icon: Box, completed: step1Completed, inProgress: !step1Completed, variant: 'default' },
            { key: 'pickup', title: '', Icon: MapPin, completed: step2Completed, inProgress: step2InProgress, variant: 'default' },
            { key: 'transit', title: '', Icon: Truck, completed: step3Completed, inProgress: step3InProgress, variant: 'default' },
            { key: 'delivery', title: '', Icon: Package, completed: step5Completed, inProgress: step5InProgress, variant: deliveryStepVariant },
          ];
        }

        return (
        <div className="mt-4 space-y-6">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Numéro d'expédition</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{trackingResult.expedition_number || '—'}</p>
                {trackingResult.order_number && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Commande : {trackingResult.order_number}</p>
                )}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-600 dark:text-gray-300">
              <div>Mode d'expédition : <span className="font-semibold text-gray-900 dark:text-white">{trackingResult.mode_expedition || '—'}</span></div>
              <div>Type de service : <span className="font-semibold text-gray-900 dark:text-white">{trackingResult.type_service || '—'}</span></div>
              <div>Mode de paiement : <span className="font-semibold text-gray-900 dark:text-white">{trackingResult.mode_paiement || '—'}</span></div>
              <div>Montant : <span className="font-semibold text-gray-900 dark:text-white">{trackingResult.montant ? `${trackingResult.montant} FCFA` : '—'}</span></div>
              <div>Transporteur : <span className="font-semibold text-gray-900 dark:text-white">{carrierName || '—'}</span></div>
              <div>Point relais : <span className="font-semibold text-gray-900 dark:text-white">{relayName || '—'}</span></div>
              {withdrawalCode && (
                <div className="sm:col-span-2 text-sm font-semibold text-ksl-red">
                  Code de retrait : {withdrawalCode}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
              <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Adresse d'expédition
              </h3>
              <div className="space-y-2 text-sm text-blue-900 dark:text-blue-100">
                <p><span className="font-medium">Nom :</span> {senderInfo.name}</p>
                <p><span className="font-medium">Téléphone :</span> {senderInfo.phone}</p>
                <p><span className="font-medium">Email :</span> {senderInfo.email}</p>
                <p><span className="font-medium">Adresse :</span> {senderInfo.address}</p>
              </div>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-100 dark:border-green-800">
              <h3 className="font-semibold text-green-900 dark:text-green-100 mb-3 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Adresse de livraison
              </h3>
              <div className="space-y-2 text-sm text-green-900 dark:text-green-100">
                {recipientInfo.company && (
                  <p><span className="font-medium">Entreprise :</span> {recipientInfo.company}</p>
                )}
                <p><span className="font-medium">Contact :</span> {recipientInfo.name}</p>
                <p><span className="font-medium">Téléphone :</span> {recipientInfo.phone}</p>
                <p><span className="font-medium">Email :</span> {recipientInfo.email}</p>
                <p><span className="font-medium">Adresse :</span> {recipientInfo.address}</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-100 dark:border-yellow-800">
            <h3 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-3 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Détails des colis
            </h3>
            {parcels.length === 0 ? (
              <p className="text-sm text-yellow-800 dark:text-yellow-200">Aucun colis renseigné.</p>
            ) : (
              <div className="space-y-3">
                {parcels.map((colis, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-yellow-200 dark:border-yellow-700">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">{colis.name || `Colis ${index + 1}`}</h4>
                      {colis.category && (
                        <span className="text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded">
                          {colis.category}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <div>Poids : {colis.weight || '--'} kg</div>
                      <div>Quantité : {colis.quantity || '--'}</div>
                      <div>Dimensions : {colis.length || '--'}×{colis.width || '--'}×{colis.height || '--'} cm</div>
                      <div>
                        Volume : {colis.length && colis.width && colis.height ? ((colis.length * colis.width * colis.height) / 1000000).toFixed(2) : '--'} m³
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Barre de progression horizontale */}
          <div className="relative mt-6 md:mt-10">
            <div className="hidden md:block">
              <div className="relative flex items-center justify-between px-6">
                <div className="absolute top-1/2 left-6 right-6 h-2 bg-gray-200 dark:bg-gray-700 rounded-full -translate-y-1/2"></div>
                <div
                  className="absolute top-1/2 left-6 h-2 bg-green-500 rounded-full transition-all duration-500 ease-out -translate-y-1/2"
                  style={{ width: `${progressLineFill}%` }}
                ></div>
                {stepsTimeline.map((step, index) => {
                  const alignmentClass =
                    index === 0
                      ? 'items-start text-left'
                      : index === stepsTimeline.length - 1
                      ? 'items-end text-right'
                      : 'items-center text-center';
                  let circleClass;
                  if (step.variant === 'cancelled') {
                    circleClass = step.completed
                      ? 'bg-red-500 border-red-500 text-white shadow-lg'
                      : step.inProgress
                      ? 'bg-white border-red-500 text-red-600'
                      : 'bg-white border-red-300 text-red-300';
                  } else if (step.variant === 'return') {
                    circleClass = step.completed
                      ? 'bg-purple-500 border-purple-500 text-white shadow-lg'
                      : step.inProgress
                      ? 'bg-white border-purple-500 text-purple-600'
                      : 'bg-white border-purple-200 text-purple-300';
                  } else if (step.variant === 'arrival') {
                    circleClass = step.completed
                      ? 'bg-blue-500 border-blue-500 text-white shadow-lg'
                      : step.inProgress
                      ? 'bg-white border-blue-500 text-blue-600'
                      : 'bg-white border-blue-200 text-blue-300';
                  } else {
                    circleClass = step.completed
                      ? 'bg-green-500 border-green-500 text-white shadow-lg'
                      : step.inProgress
                      ? 'bg-white border-green-500 text-green-500'
                      : 'bg-white border-gray-300 text-gray-300';
                  }
                  const shiftClass =
                    index === 0
                      ? 'md:-translate-x-1/2 md:self-start md:transform'
                      : index === stepsTimeline.length - 1
                      ? 'md:translate-x-1/2 md:self-end md:transform'
                      : '';
                  return (
                    <div
                      key={step.key}
                      className={`relative flex flex-col flex-1 ${alignmentClass} gap-3`}
                    >
                      <div className="relative flex items-center justify-center w-full">
                        <div className="relative z-10 flex items-center justify-center">
                          <div
                            className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 shadow-md ${circleClass} ${shiftClass}`}
                          >
                            <step.Icon className="w-5 h-5" />
                          </div>
                        </div>
                      </div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{step.title}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className={`flex flex-col gap-8 md:grid ${totalSteps === 4 ? 'md:grid-cols-4' : 'md:grid-cols-5'} md:gap-6 mt-6 md:mt-10`}>
            {/* Commandé */}
            <div className="relative flex flex-col items-center text-center group animate-fade-in md:flex-1 md:items-start md:text-left" style={{ animationDelay: '0.1s' }} >
              <div className={`z-10 flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white shadow-lg ${step1Completed ? 'bg-green-500' : 'bg-gray-300'} md:hidden`}>
                <Box className="w-6 h-6" />
              </div>
              <div className={`mt-4 md:mt-0 w-full rounded-xl p-6 border-2 shadow-lg ${step1Completed ? 'bg-white dark:bg-gray-800 border-green-200 dark:border-green-700' : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${step1Completed ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>1</span>
                    Commandé
                  </h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${step1Completed ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                    {step1Completed ? '✓ Terminé' : '⏳ En attente'}
                  </span>
                </div>
                <p className={`text-sm mb-4 ${step1Completed ? 'text-green-700 dark:text-green-300' : 'text-gray-600 dark:text-gray-400'}`}>
                  {globalStatus === 'En chemin vers le lieu de ramassage' || globalStatus === 'En chemin vers la destination' || globalStatus === 'Arrivé à destination' || globalStatus === 'Terminé'
                    ? "Votre colis a été préparé et est prêt pour l'expédition."
                    : globalStatus === 'Planifié'
                    ? "Votre colis est actuellement en cours de préparation par nos équipes."
                    : "Votre colis sera préparé prochainement."}
                </p>
              </div>
            </div>

            {/* Ramassage */}
            <div className="relative flex flex-col items-center text-center group animate-fade-in md:flex-1 md:items-center" style={{ animationDelay: '0.1s' }}>
              <div className={`z-10 flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white shadow-lg ${step2Completed ? 'bg-green-500' : 'bg-gray-300'} md:hidden`}>
                <MapPin className="w-6 h-6" />
              </div>
              <div className={`mt-4 md:mt-0 w-full rounded-xl p-6 border-2 shadow-lg ${step2Completed ? 'bg-white dark:bg-gray-800 border-green-200 dark:border-green-700' : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${step2Completed ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>2</span>
                    Ramassage
                  </h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${step2Completed ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                    {step2Completed ? '✓ Terminé' : globalStatus === 'Arrivé au lieu de ramassage' ? '🚚 En cours' : '⏳ En attente'}
                  </span>
                </div>
                <p className={`text-sm mb-4 ${step2Completed ? 'text-green-700 dark:text-green-300' : 'text-gray-600 dark:text-gray-400'}`}>
                  {globalStatus === 'En chemin vers la destination' || globalStatus === 'Arrivé à destination' || globalStatus === 'Terminé'
                    ? 'Votre colis a été réceptionné par le transporteur et est en cours de livraison.'
                    : globalStatus === 'Arrivé au lieu de ramassage'
                    ? "Un transporteur est en arrivé au lieu de ramassage."
                    : "Le transporteur sera en arrivé au lieu de ramassage prochainement."}
                </p>
              </div>
            </div>

            {/* Transit */}
            <div className="relative flex flex-col items-center text-center group animate-fade-in md:flex-1 md:items-center" style={{ animationDelay: '0.1s' }}>
              <div className={`z-10 flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white shadow-lg ${step3Completed ? 'bg-green-500' : 'bg-gray-300'} md:hidden`}>
                <Truck className="w-6 h-6" />
              </div>
              <div className={`mt-4 md:mt-0 w-full rounded-xl p-6 border-2 shadow-lg ${step3Completed ? 'bg-white dark:bg-gray-800 border-green-200 dark:border-green-700' : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${step3Completed ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>3</span>
                    En transit
                  </h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${step3Completed ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                    {step3Completed ? '✓ Terminé' : step3InTransit ? '🚚 En cours' : '⏳ En attente'}
                  </span>
                </div>
                <p className={`text-sm mb-4 ${step3Completed ? 'text-green-700 dark:text-green-300' : 'text-gray-600 dark:text-gray-400'}`}>
                  {globalStatus === 'En Chemin vers la destination'
                    ? "Votre colis est actuellement en transit vers sa destination."
                    : globalStatus === 'Arrivé à destination' || globalStatus === 'Terminé'
                    ? "Le transit a été terminé. Le colis est arrivé à destination."
                    : "Le colis sera mis en transit après le ramassage."}
                </p>
              </div>
            </div>

            {/* Étape 4 : Arrivée à destination (Point Relais avant livraison) OU Livraison (Domicile ou Point Relais après livraison) */}
            {isRelayMode && !((statusExp?.toLowerCase().trim() === 'expédié' || statusExp?.toLowerCase().trim() === 'expedie') || (statusRecep?.toLowerCase().trim() === 'livré' || statusRecep?.toLowerCase().trim() === 'livre')) ? (
              // Mode Point Relais avant livraison : Afficher "Arrivée à destination"
              <div className="relative flex flex-col items-center text-center group animate-fade-in md:flex-1 md:items-end md:text-right" style={{ animationDelay: '0.1s' }}>
              <div className={`z-10 flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white shadow-lg ${step4Completed || step4InProgress ? 'bg-blue-500' : 'bg-gray-300'} md:hidden`}>
                <MapPin className="w-6 h-6" />
              </div>
              <div className={`mt-4 md:mt-0 w-full rounded-xl p-6 border-2 shadow-lg ${step4Completed || step4InProgress ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700' : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${step4Completed || step4InProgress ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>4</span>
                    Arrivée à destination
                  </h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${step4Completed ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' : step4InProgress ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                    {arrivalBadgeLabel}
                  </span>
                </div>
                <p className={`text-sm mb-4 ${step4Completed || step4InProgress ? 'text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400'}`}>
                  {arrivalMessageText}
                </p>
              </div>
            </div>
            ) : (
              // Mode Domicile OU Mode Point Relais après livraison : Afficher "Livraison"
            <div className="relative flex flex-col items-center text-center group animate-fade-in md:flex-1 md:items-end md:text-right" style={{ animationDelay: '0.1s' }}>
                {(() => {
                  const statusExpLower = statusExp?.toLowerCase().trim() || '';
                  const statusRecepLower = statusRecep?.toLowerCase().trim() || '';
                  const isDeliveredForDisplay = (statusExpLower === 'expédié' || statusExpLower === 'expedie') || 
                                                 (statusRecepLower === 'livré' || statusRecepLower === 'livre');
                  const isCompleted = isRelayMode ? isDeliveredForDisplay : step5Completed;
                  return (
                    <>
                      <div className={`z-10 flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white shadow-lg ${isCompleted ? 'bg-green-500' : isCancelledStatus ? 'bg-red-500' : isReturnStatus ? 'bg-purple-500' : 'bg-gray-300'} md:hidden`}>
                        <Package className="w-6 h-6" />
                      </div>
                      <div className={`mt-4 md:mt-0 w-full rounded-xl p-6 border-2 shadow-lg ${
                        isCompleted 
                          ? 'bg-white dark:bg-gray-800 border-green-200 dark:border-green-700' 
                          : isCancelledStatus 
                          ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700' 
                          : isReturnStatus 
                          ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700' 
                          : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                      }`}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                              isCompleted 
                                ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400' 
                                : isCancelledStatus 
                                ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400' 
                                : isReturnStatus 
                                ? 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400' 
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                            }`}>4</span>
                            {deliveryCardTitle}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            isCompleted 
                              ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                              : isCancelledStatus 
                              ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300' 
                              : isReturnStatus 
                              ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300' 
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                          }`}>
                            {isCompleted ? '✅ Terminé' : deliveryBadgeLabel}
                          </span>
                        </div>
                        <p className={`text-sm mb-4 ${
                          isCompleted 
                            ? 'text-green-700 dark:text-green-300' 
                            : isCancelledStatus 
                            ? 'text-red-600 dark:text-red-400' 
                            : isReturnStatus 
                            ? 'text-purple-600 dark:text-purple-400' 
                            : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {isCompleted ? "Votre colis a été livré avec succès à l'adresse de destination." : deliveryMessageText}
                        </p>
                      </div>
                    </>
                  );
                })()}
            </div>
            )}

          </div>
        </div>
        );
      })()}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gradient-to-br from-ksl-gray-dark via-gray-800 to-ksl-black text-white relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>

        <div className="container-ksl py-16 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* LMS Katian */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-white to-ksl-gray-light bg-clip-text text-transparent">
               KSL
              </h3>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-ksl-red to-ksl-red-dark rounded-full flex items-center justify-center border-2 border-white shadow-lg transform hover:scale-110 transition-transform duration-300">
                  <span className="text-white font-bold text-lg">K</span>
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-white to-ksl-gray-light bg-clip-text text-transparent">
                  KATIAN
                </span>
              </div>
              <p className="text-ksl-gray-light text-sm leading-relaxed">
                La solution logistique innovante pour une gestion simplifiée de vos expéditions.
              </p>
              <div className="flex space-x-4">
                <div className="w-2 h-2 bg-ksl-red rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-ksl-red rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                <div className="w-2 h-2 bg-ksl-red rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
              </div>
            </div>

            {/* Liens Rapides */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-white to-ksl-gray-light bg-clip-text text-transparent">
                Liens Rapides
              </h3>
              <ul className="space-y-3">
                {[
                  { to: '/solutions', label: 'Solutions' },
                  { to: '/fonctionnalites', label: 'Fonctionnalités' },
                  { to: '/tarifs', label: 'Tarifs' },
                  { to: '/devenir-relais', label: 'Devenir Point Relais' },
                  { to: '/support', label: 'Support & Aide' },
                  // { to: '/a-propos', label: 'À propos' }
                ].map((link, index) => (
                  <li key={link.to}>
                    <Link 
                      to={link.to} 
                      className="text-ksl-gray-light hover:text-white transition-all duration-300 text-sm group flex items-center"
                    >
                      <span className="w-0 h-0.5 bg-ksl-red group-hover:w-4 transition-all duration-300 mr-2"></span>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Nous Contacter */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-white to-ksl-gray-light bg-clip-text text-transparent">
                Nous Contacter
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-4 group">
                  <div className="w-8 h-8 bg-gradient-to-br from-ksl-red to-ksl-red-dark rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-ksl-gray-light text-sm group-hover:text-white transition-colors duration-300">
                    Immeuble Palladium, Bel Air, Abidjan
                  </span>
                </div>
                <div className="flex items-center space-x-4 group">
                  <div className="w-8 h-8 bg-gradient-to-br from-ksl-gray to-ksl-gray-dark rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <Phone className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-ksl-gray-light text-sm group-hover:text-white transition-colors duration-300">
                    +225 01 60 00 55 55
                  </span>
                </div>
                <div className="flex items-center space-x-4 group">
                  <div className="w-8 h-8 bg-gradient-to-br from-ksl-gray to-ksl-gray-dark rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <Phone className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-ksl-gray-light text-sm group-hover:text-white transition-colors duration-300">
                    +225 05 66 03 02 02
                  </span>
                </div>
                <div className="flex items-center space-x-4 group">
                  <div className="w-8 h-8 bg-gradient-to-br from-ksl-red to-ksl-red-dark rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <Mail className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-ksl-gray-light text-sm group-hover:text-white transition-colors duration-300">
                  contact@katianlogistique.com
                  </span>
                </div>
                <div className="pt-2">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center text-ksl-red hover:text-ksl-red-light transition-all duration-300 text-sm group"
                  >
                    <span className="underline group-hover:no-underline">Formulaire de contact</span>
                    <span className="ml-1 group-hover:ml-2 transition-all duration-300">→</span>
                  </Link>
                </div>
              </div>

              {/* Schéma de suivi complet et intuitif */}
              <div className="hidden relative mt-6">
                <div className="space-y-6">
                  {/* Étape 1: Commande créée */}
                  <div className="relative flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center z-10 relative shadow-lg">
                      <Package className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-8 flex-1">
                      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border-2 border-green-200 dark:border-green-700 shadow-lg">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <span className="w-6 h-6 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center text-green-600 dark:text-green-400 text-sm font-bold">1</span>
                            Commande créée
                          </h3>
                          <span className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-sm font-medium">✓ Terminé</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div className="space-y-2">
                            <p className="text-gray-600 dark:text-gray-300"><span className="font-semibold">Numéro:</span> {trackingResult?.order_number || trackingResult?.expedition_number || '—'}</p>
                            <p className="text-gray-600 dark:text-gray-300"><span className="font-semibold">Date:</span> {trackingResult?.date_creation ? new Date(trackingResult?.date_creation).toLocaleString('fr-FR') : '—'}</p>
                          </div>
                          <div className="space-y-2">
                            <p className="text-gray-600 dark:text-gray-300"><span className="font-semibold">Type:</span> {trackingResult?.type_colis || trackingResult?.infocolis?.[0]?.category || '—'}</p>
                            <p className="text-gray-600 dark:text-gray-300"><span className="font-semibold">Service:</span> {trackingResult?.type_service || '—'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Étape 2: Préparation */}
                  <div className="relative flex items-start">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center z-10 relative shadow-lg ${trackingResult?.statut_colis === 'préparé' ? 'bg-yellow-500' : 'bg-gray-300'}`}>
                      <Box className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-8 flex-1">
                      <div className={`rounded-xl p-6 border-2 shadow-lg ${trackingResult?.statut_colis === 'préparé' ? 'bg-white dark:bg-gray-800 border-yellow-200 dark:border-yellow-700' : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'}`}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${trackingResult?.statut_colis === 'préparé' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>2</span>
                            Préparation du colis
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${trackingResult?.statut_colis === 'préparé' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                            {trackingResult?.statut_colis === 'préparé' ? '✓ Terminé' : '⏳ En attente'}
                          </span>
                        </div>
                        {Array.isArray(trackingResult?.infocolis) && trackingResult?.infocolis.length > 0 && (
                          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                            <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-3">Détails des colis:</h4>
                            <div className="space-y-3">
                              {trackingResult?.infocolis.map((colis, index) => (
                                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-yellow-200 dark:border-yellow-700">
                                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
                                    <div>Poids: {colis.weight} kg</div>
                                    <div>Quantité: {colis.quantity}</div>
                                    <div>Dimensions: {colis.length}×{colis.width}×{colis.height} cm</div>
                                    <div>Volume: {colis.length && colis.width && colis.height ? (colis.length * colis.width * colis.height / 1000000).toFixed(2) : '--'} m³</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Étape 3: Ramassage */}
                  <div className="relative flex items-start">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center z-10 relative shadow-lg ${trackingResult?.statut === 'en attente' ? 'bg-orange-500' : 'bg-gray-300'}`}>
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-8 flex-1">
                      <div className={`rounded-xl p-6 border-2 shadow-lg ${trackingResult?.statut === 'en attente' ? 'bg-white dark:bg-gray-800 border-orange-200 dark:border-orange-700' : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'}`}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${trackingResult?.statut === 'en attente' ? 'bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>3</span>
                            Ramassage
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${trackingResult?.statut === 'en attente' ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                            {trackingResult?.statut === 'en attente' ? '🔄 En cours' : '⏳ En attente'}
                          </span>
                        </div>
                        {trackingResult?.adresse_expediteur && (
                          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                            <div className="space-y-3">
                              <div>
                                <h4 className="font-semibold text-orange-900 dark:text-orange-100 mb-2">📍 Adresse de ramassage</h4>
                                <p className="text-sm text-orange-700 dark:text-orange-300">{trackingResult?.adresse_expediteur?.address || '—'}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Étape 4: En transit */}
                  <div className="relative flex items-start">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center z-10 relative shadow-lg ${trackingResult?.statut === 'en transit' ? 'bg-blue-500' : 'bg-gray-300'}`}>
                      <Truck className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-8 flex-1">
                      <div className={`rounded-xl p-6 border-2 shadow-lg ${trackingResult?.statut === 'en transit' ? 'bg-white dark:bg-gray-800 border-blue-200 dark:border-blue-700' : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'}`}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${trackingResult?.statut === 'en transit' ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>4</span>
                            En transit
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${trackingResult?.statut === 'en transit' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                            {trackingResult?.statut === 'en transit' ? '🚚 En cours' : '⏳ En attente'}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{trackingResult?.dataApi?.merged_est_dist_km || '--'}</div>
                            <div className="text-xs text-blue-700 dark:text-blue-300">km</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{trackingResult?.dataApi?.merged_est_minutes || '--'}</div>
                            <div className="text-xs text-blue-700 dark:text-blue-300">min</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-blue-600 dark:text-blue-400">{trackingResult?.delais_livraison || '--'}</div>
                            <div className="text-xs text-blue-700 dark:text-blue-300">délai</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Étape 5: Livraison */}
                  <div className="relative flex items-start">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center z-10 relative shadow-lg ${trackingResult?.statut === 'livré' ? 'bg-green-500' : 'bg-gray-300'}`}>
                      <Package className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-8 flex-1">
                      <div className={`rounded-xl p-6 border-2 shadow-lg ${trackingResult?.statut === 'livré' ? 'bg-white dark:bg-gray-800 border-green-200 dark:border-green-700' : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'}`}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${trackingResult?.statut === 'livré' ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>5</span>
                            Livraison
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${trackingResult?.statut === 'livré' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                            {trackingResult?.statut === 'livré' ? '✅ Terminé' : '⏳ En attente'}
                          </span>
                        </div>
                        {trackingResult?.adresse_destinataire && (
                          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                            <div className="space-y-3">
                              <div>
                                <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">📍 Adresse de livraison</h4>
                                <p className="text-sm text-green-700 dark:text-green-300">{trackingResult?.adresse_destinataire?.address || '—'}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Statut global */}
                  <div className="relative flex items-start">
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center z-10 relative shadow-lg ${getStatusColor(trackingResult?.statut)}`}>
                      <Package className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-8 flex-1">
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-6 border-2 border-purple-200 dark:border-purple-700 shadow-lg">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <span className="w-6 h-6 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-400 text-sm font-bold">📊</span>
                            Statut global
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(trackingResult?.statut).replace('bg-', 'text-')}`}>
                            {trackingResult?.statut || '—'}
                          </span>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">Statut du colis</p>
                              <p className="text-gray-600 dark:text-gray-300">{trackingResult?.statut_colis || '—'}</p>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">Point relais</p>
                              <p className="text-gray-600 dark:text-gray-300">{trackingResult?.relay_name || trackingResult?.point_relais?.name || trackingResult?.point_relais_nom || (trackingResult?.pointrelais != null ? String(trackingResult?.pointrelais) : '—')}</p>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">Transporteur</p>
                              <p className="text-gray-600 dark:text-gray-300">{trackingResult?.carrier_name || trackingResult?.transporteur?.nom || trackingResult?.transporteur_nom || (trackingResult?.transporteur != null ? String(trackingResult?.transporteur) : '—')}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Suivez-nous & Newsletter */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-white to-ksl-gray-light bg-clip-text text-transparent mb-6">
                  Suivez-nous
                </h3>
                <div className="flex space-x-4">
                  {[
                    { icon: Facebook, color: 'from-blue-600 to-blue-700', href: '#' },
                    { icon: Instagram, color: 'from-pink-500 to-purple-600', href: '#' },
                    { icon: Twitter, color: 'from-blue-400 to-blue-500', href: '#' },
                    { icon: Linkedin, color: 'from-blue-700 to-blue-800', href: '#' },
                    { icon: MessageCircle, color: 'from-ksl-red to-ksl-red-dark', href: '#' }
                  ].map((social, index) => (
                    <a 
                      key={index}
                      href={social.href} 
                      className={`w-12 h-12 bg-gradient-to-br ${social.color} rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 group`}
                    >
                      <social.icon className="w-5 h-5 text-white group-hover:rotate-12 transition-transform duration-300" />
                    </a>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-white to-ksl-gray-light bg-clip-text text-transparent mb-6">
                  Newsletter
                </h3>
                <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                  <div className="relative">
                    <input
                      type="email"
                      placeholder="Votre email"
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-ksl-gray-dark/50 backdrop-blur-sm border border-ksl-gray rounded-xl text-white placeholder-ksl-gray-light focus:outline-none focus:ring-2 focus:ring-ksl-red focus:border-transparent transition-all duration-300"
                      required
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent rounded-xl pointer-events-none"></div>
                  </div>
                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-gradient-to-r from-ksl-red to-ksl-red-dark text-white rounded-xl hover:from-ksl-red-dark hover:to-ksl-black transition-all duration-300 font-medium shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
                  >
                    S'inscrire
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-ksl-gray/30 py-8 relative z-10">
          <div className="container-ksl text-center">
            <p className="text-ksl-gray-light text-sm">
              © 2025 LMS Katian. Tous droits réservés.
            </p>
            <div className="mt-4 flex justify-center space-x-6 text-xs text-ksl-gray">
              <a href="#" className="hover:text-ksl-gray-light transition-colors duration-300">Mentions légales</a>
              <a href="#" className="hover:text-ksl-gray-light transition-colors duration-300">Politique de confidentialité</a>
              <a href="#" className="hover:text-ksl-gray-light transition-colors duration-300">CGU</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
} 